import {AxiosResponse} from "axios";
import React from "react";
import {NavLink} from "react-router-dom";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import Loader from "../../../../common/components/shared/loader/loader";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import Table, {TableActionFunction} from "../../../../common/components/shared/table/table";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {Api} from "../../../../helpers/api";
import {DateUtils} from "../../../../helpers/dateUtils";
import {routingAddresses} from "../../../../helpers/routing";
import i18n from "../../../../i18n/i18n";
import {GeneralDeclarationModel} from "../../../models/generalDeclaration";
import {GeneralDeclarationService} from "../../../services/generalDeclarationService";
import "../generalDeclaration.scss";
import "../../documents.scss";
import {SortDirection} from "../../../../common/models/table";
import Date from "../../../../timetables/components/actionForm/dateAndTime/date";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import {DropDownUtils} from "../../../../helpers/dropDownUtils";
import {ShipownerModel} from "../../../../dictionaries/models/shipowners";
import {ShipownerService} from "../../../../dictionaries/services/shipownerService";
import {FerryDropDownModel} from "../../../../dictionaries/models/ferry";
import {FerryService} from "../../../../dictionaries/services/ferryService";
import {DropDownModel} from "../../../../common/models/dropDown";
import {DirectionTypes} from "../../../models/enums";
import DocumentsDirectionTypeDropdownItems from "../../../../helpers/documentsDirectionTypeDropDownItems";
import {storesContext} from "../../../../common/stores/stores";
import {IPaginationStore} from "../../../../common/stores/paginationStore";
import SearchButton from "../../../../common/components/shared/searchButton/searchButton";

interface GeneralDeclarationListState {
    declarationData: GeneralDeclarationModel[] | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    orderBy?: string;
    sortDirection?: SortDirection;
    arrivalDepartureTime: ValidatedValue<Date | null>;
    typeFilter: ValidatedValue<DirectionTypes | "">;
    shipownerFilter: ValidatedValue<string>;
    ferryFilter: ValidatedValue<string>;
    arrivalDepartureTimeFilter?: string;
    currentPage: number;
    isTableLoader: boolean;
    shipowners: ShipownerModel[];
    ferries: FerryDropDownModel[];
    isFilterWaiting: boolean;
    shipownersResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    ferriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

export default class GeneralDeclarationList extends React.Component<any, GeneralDeclarationListState> {
    static contextType = storesContext;
    private sortableElements = ["voyageNo"];

    constructor(props: any) {
        super(props);

        this.state = {
            declarationData: undefined,
            response: undefined,
            orderBy: undefined,
            sortDirection: undefined,
            arrivalDepartureTime: new ValidatedValue(null),
            arrivalDepartureTimeFilter: undefined,
            typeFilter: new ValidatedValue(""),
            shipownerFilter: new ValidatedValue(""),
            ferryFilter: new ValidatedValue(""),
            currentPage: 0,
            isTableLoader: false,
            shipowners: [],
            ferries: [],
            isFilterWaiting: false,
            shipownersResponse: undefined,
            ferriesResponse: undefined
        };
    }

    get declarationData() {
        return this.state.declarationData;
    }

    get isCorrectArrivalDepartureTime() {
        const arrivalDepartureTime = this.state.arrivalDepartureTime.value;

        return arrivalDepartureTime && arrivalDepartureTime.getFullYear() &&
            Validator.isDateIsNumber(arrivalDepartureTime) && arrivalDepartureTime.getFullYear().toString().length === 4;
    }

    public async componentDidMount() {
        await this.getFerries();
        await this.getShipowners();

        await this.getAllGeneralDeclaration();
    }

    public render(): JSX.Element {
        const properties = ["directionType", "shipownerName", "ferryName", "ferryImoCode", "voyageNo",
            "arrivalDeparturePort", "arrivalDepartureTime", "originDestinationPort"];
        const headers = properties.map(key => i18n.t("documents.generalDeclaration.headers." + key));
        const actions = this.renderActions;

        return (
            <div className="general-declaration">
                {this.renderWrapperContent(headers, properties, "general-declaration", actions, this.renderCell)}
            </div>
        );
    }

    private renderHeader() {
        return (
            <div className="header">
                <div className="filters-content">
                    {this.renderFilters()}
                </div>
                <div className="actions">
                    <SearchButton
                        isWaiting={this.state.isFilterWaiting}
                        onClick={async () => {
                            this.setState({isFilterWaiting: true});
                            await this.filterByArrivalDepartureTime();
                            this.setState({isFilterWaiting: false});
                        }}
                    />
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="general-declaration-list-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderFilters() {
        return <div className="filters">
            <DropDown
                label={i18n.t("documents.generalDeclaration.filters.type")}
                validatedValue={this.state.typeFilter}
                items={DocumentsDirectionTypeDropdownItems.getDocumentsDirectionTypeDropdownItems()}
                testId="filters-type"
                withEmptyValue
            />
            <DropDown
                label={i18n.t("documents.generalDeclaration.filters.shipowner")}
                validatedValue={this.state.shipownerFilter}
                items={this.shipownersToDropDownMap}
                testId="filters-shipowner"
                withEmptyValue
                error={!Api.isSuccess(this.state.shipownersResponse) ? i18n.t("errorMessages.shipownersErrorSuffix") : ""}
                reloadFunc={() => this.getShipowners()}
                isNoRequired
            />
            <DropDown
                label={i18n.t("documents.generalDeclaration.filters.ferry")}
                validatedValue={this.state.ferryFilter}
                items={this.ferryToDropDownMap}
                testId="filters-ferry"
                withEmptyValue
                error={!Api.isSuccess(this.state.ferriesResponse) ? i18n.t("errorMessages.ferriesErrorSuffix") : ""}
                reloadFunc={() => this.getFerries()}
                isNoRequired
            />
            <Date
                testId="filters-arrival-departure-time"
                dateLabel={i18n.t("documents.generalDeclaration.filters.arrivalDepartureTime")}
                dateValue={this.state.arrivalDepartureTime}
            />
        </div>;
    }

    private renderWrapperContent(headers: string[], properties: string[], testId: string, actions?: TableActionFunction, renderCell?: (row: any, property: string) => JSX.Element) {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("documents.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.declarationData === undefined) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {this.renderHeader()}
                        <div className="general-declaration-content">
                            {this.declarationData && this.declarationData!.length && this.declarationData!.length > 0 ? (
                                <Table
                                    headers={headers}
                                    rows={this.state.declarationData!}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    actions={actions ? actions.bind(this) : actions}
                                    renderCell={renderCell}
                                    pageable={true}
                                    sortableElements={this.sortableElements}
                                    sortingAction={async (orderBy: string, sortDirection: SortDirection) => {
                                        await this.setState({orderBy, sortDirection});
                                        await this.getAllGeneralDeclaration();
                                    }}
                                    orderBy={this.state.orderBy}
                                    sortDirection={this.state.sortDirection}
                                    currentPage={this.state.currentPage}
                                    isOldPagination={false}
                                    isTableLoader={this.state.isTableLoader}
                                    onPageChange={this.getAllGeneralDeclaration.bind(this)}
                                />
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    numberOfElements={0}
                                    currentPage={0}
                                />
                            )}
                        </div>
                    </>
                );
            }
        }
    }

    private renderActions(element: GeneralDeclarationModel) {
        // @ts-ignore
        const elementId = element.id;

        return (
            <>
                {elementId && (
                    <>
                        <NavLink
                            to={`${routingAddresses.documents.generalDeclaration.details}/${elementId}`}
                            className="preview-button"
                            data-testid={`preview-${elementId}`}
                        >
                            {i18n.t("documents.generalDeclaration.preview")}
                        </NavLink>
                    </>
                )}
            </>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        if (property === "directionType") {
            return <>{i18n.t(`directionType.${row[property]}`)}</>;
        } else if (property === "arrivalDepartureTime") {
            return <>{DateUtils.getFormattedDateWithTime((row[property]))}</>;
        }

        return row[property];
    }

    private async reload() {
        this.setState({declarationData: undefined});
        await this.getAllGeneralDeclaration();
    }

    private async filterByArrivalDepartureTime() {
        if (!this.state.arrivalDepartureTime.value) {
            await this.setState({arrivalDepartureTimeFilter: undefined});
            await this.getAllGeneralDeclaration();
        }

        if (this.isCorrectArrivalDepartureTime) {
            const {startDate, endDate} = DateUtils.getTimeIntervalFromDate(this.state.arrivalDepartureTime.value!);

            await this.setState({arrivalDepartureTimeFilter: `arrivalDepartureTime>=${startDate},arrivalDepartureTime<${endDate}`});
            await this.getAllGeneralDeclaration();
        }
    }

    private async getAllGeneralDeclaration() {
        const paginationStore = this.context.paginationStore as IPaginationStore;
        this.setState({
            isTableLoader: true,
            declarationData: undefined
        });

        const filters = {
            type: this.state.typeFilter.value,
            shipownerId: this.state.shipownerFilter.value,
            ferryId: this.state.ferryFilter.value,
            arrivalDepartureTime: this.state.arrivalDepartureTimeFilter ? this.state.arrivalDepartureTimeFilter : ""
        };

        const response = await GeneralDeclarationService.getAll(
            filters,
            {
                offset: paginationStore.offset,
                size: paginationStore.size
            },
            {
                orderBy: this.state.orderBy,
                sortDirection: this.state.sortDirection
            }
        );

        this.setState({
            declarationData: response?.data?.data || null,
            response,
            currentPage: response?.data?.currentPage,
            isTableLoader: false
        });

        paginationStore.setNumberOfElements(response?.data?.numberOfElements);
        paginationStore.setNumberOfPages(response?.data?.numberOfPages);
    }

    private async getFerries() {
        const ferriesResponse = await FerryService.getFerriesForDropDown();

        this.setState({
            ferries: (ferriesResponse)?.data || [],
            ferriesResponse,
        });
    }

    private async getShipowners() {
        const shipownersResponse = await ShipownerService.getAll();

        this.setState({
            shipowners: (shipownersResponse)?.data?.data || [],
            shipownersResponse
        });
    }

    public get shipownersToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.shipowners, "entityId");
    }

    private get ferryToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.ferries, "entityId");
    }
}
